import type { CatchballLoop } from '../../../types/prioritization/catchballLoop';

/**
 * Pick the loop the Request Composer operates on.
 *
 * Multi-path Cycle Designer launches create ONE loop PER TRACK, and the
 * `catchball_create_loop` RPC numbers loops per track (`max(loop_number)
 * WHERE track_id = …`) — so every loop in a fresh multi-path launch shares
 * `loopNumber = 1`. Any selection that orders by loopNumber alone therefore
 * ties, and the tie used to fall to the store's array order, which mirrored
 * an unordered `SELECT` — effectively random, re-shuffled on every refetch.
 * That is how a request composed under one path's label could silently
 * target the OTHER path's loop, delivering each path's request to the other
 * path's owner (path-to-owner routing swap, owner report 2026-07-18).
 *
 * Rules:
 *  - `designerTrackId` present (Cycle Designer launch pins `?trackId=`):
 *    ONLY that track's non-closed loop may be returned. Never fall through
 *    to a different track's loop — a wrong loop under a pinned track badge
 *    IS the cross-routing bug. Returns null until that loop loads.
 *  - No `designerTrackId`: the most recently created non-closed loop
 *    (loopNumber DESC, then createdAt DESC as the deterministic tie-break),
 *    falling back to any drafting loop — the same preference order the
 *    Composer always had, now deterministic under equal loop numbers.
 */
export function selectComposerLoop(
  loops: CatchballLoop[],
  cycleId: string | undefined,
  designerTrackId?: string,
): CatchballLoop | null {
  if (!cycleId) return null;

  const cycleLoops = loops
    .filter((entry) => entry.cycleId === cycleId)
    .sort(
      (left, right) =>
        right.loopNumber - left.loopNumber ||
        (right.createdAt?.getTime?.() ?? 0) - (left.createdAt?.getTime?.() ?? 0),
    );

  if (designerTrackId) {
    return (
      cycleLoops.find(
        (entry) => entry.trackId === designerTrackId && entry.state !== 'closed',
      ) ?? null
    );
  }

  // Prefer the most recently created non-closed loop, not just "any drafting
  // loop" — an abandoned early draft (e.g. a duplicate created by a
  // load-order race) must never outrank a later loop that already has an
  // issued request / Balls in flight. Only fall back to an older drafting
  // loop if no non-closed loop exists at all.
  return (
    cycleLoops.find((entry) => entry.state !== 'closed') ||
    cycleLoops.find((entry) => entry.state === 'drafting') ||
    null
  );
}
